import { z } from 'zod';
import type { SalesPersonToken } from '@sleed/bizcommerce-api';

/**
 * Sales-person edit form. Mirrors `SalesPersonToken` and the legacy admin grid popup
 * (Core/client/admin/src/pages/Customers/SalesPersons/index.js): usr required ≤255,
 * psw required on create (optional on edit — blank keeps the current password), fn/ln ≤50,
 * eml email ≤255, act/cfm flags, erpcd ≤50, rspid replacement sales-person FK, rsd/red
 * replacement window dates, iv/si visibility+sort. The optional admin-user link (`userId`) is omitted.
 */
export const salesPersonSchema = z
	.object({
		id: z.number().optional(),
		usr: z.string().trim().min(1, 'validation.required').max(255, 'validation.tooLong'),
		psw: z.string().nullable(),
		fn: z.string().trim().max(50, 'validation.tooLong').nullable(),
		ln: z.string().trim().max(50, 'validation.tooLong').nullable(),
		eml: z.union([z.literal(''), z.string().trim().email('validation.email').max(255, 'validation.tooLong')]).nullable(),
		act: z.boolean(),
		cfm: z.boolean(),
		erpcd: z.string().trim().max(50, 'validation.tooLong').nullable(),
		rspid: z.number().nullable(),
		rsd: z.string().nullable(),
		red: z.string().nullable(),
		iv: z.boolean(),
		si: z.number().int()
	})
	// Password is mandatory only when creating a new sales person.
	.refine(v => Boolean(v.id) || Boolean(v.psw && v.psw.length > 0), {
		path: ['psw'],
		message: 'validation.required'
	});

export type SalesPersonFormValues = z.infer<typeof salesPersonSchema>;

export const EMPTY_SALES_PERSON: SalesPersonFormValues = {
	usr: '',
	psw: '',
	fn: '',
	ln: '',
	eml: '',
	act: true,
	cfm: false,
	erpcd: '',
	rspid: null,
	rsd: null,
	red: null,
	iv: true,
	si: 0
};

export function tokenToForm(token: SalesPersonToken): SalesPersonFormValues {
	return {
		id: token.id,
		usr: token.usr ?? '',
		psw: '',
		fn: token.fn ?? '',
		ln: token.ln ?? '',
		eml: token.eml ?? '',
		act: token.act ?? false,
		cfm: token.cfm ?? false,
		erpcd: token.erpcd ?? '',
		rspid: token.rspid ?? null,
		rsd: (token.rsd as string | null) ?? null,
		red: (token.red as string | null) ?? null,
		iv: token.iv ?? true,
		si: token.si ?? 0
	};
}

export function formToToken(form: SalesPersonFormValues, original: SalesPersonToken | null): SalesPersonToken {
	return {
		...(original ?? {}),
		id: form.id,
		usr: form.usr.trim(),
		// Blank password on edit → omit so the server keeps the existing one.
		...(form.psw ? { psw: form.psw } : original ? {} : { psw: form.psw }),
		fn: form.fn?.trim() || null,
		ln: form.ln?.trim() || null,
		eml: form.eml?.trim() || null,
		act: form.act,
		cfm: form.cfm,
		erpcd: form.erpcd?.trim() || null,
		rspid: form.rspid ?? null,
		rsd: form.rsd,
		red: form.red,
		iv: form.iv,
		si: form.si
	};
}
